import { Box } from '@mui/material'
import { Loader } from '@tom-ui/ui'
import { useYardStores } from '@yard/AppProvider'
import { observer } from 'mobx-react-lite'
import { ReactNode, useEffect } from 'react'
import { BlockAccessPointsPanel } from './components/organisms/BlockAccessPointsPanel'
import { YardBlockContainers } from './components/organisms/YardBlockContainers'
import { YardDrawers } from './components/organisms/YardDrawers'
import { YardFormDialogs } from './components/organisms/YardFormDialogs'
import { YardHeader } from './components/organisms/YardHeader'
import { YardLayout } from './components/organisms/YardLayout'
import { useCanvasWidthGuard } from './hooks/use-canvas-width-guard'
import { useContainerDetailsDrawerWidth } from './hooks/use-container-details-drawer-width'
import { useViewportWidth } from './hooks/use-viewport-width'
import { useYardSetup } from './hooks/use-yard-setup'

export interface YardWarningThresholds {
  allocationRule: number | null
  block: number | null
  yard: number | null
}

interface Props {
  renderContainerDetailsByJourneyId: (
    journeyId: number,
    highlight?: boolean,
    isReserved?: boolean,
    schemeBackgroundColor?: string,
  ) => ReactNode
  getJourneyByContainerIds: (ids: number[]) => Promise<void>
  companyAutoComplete: (props: any) => ReactNode
  locationCodeAutoComplete: (props: any) => ReactNode
  imoClassAutoComplete: (props: any) => ReactNode
  transportSelection: (props: any) => ReactNode
  warningThresholds?: YardWarningThresholds
}

export const Yard = observer(
  ({
    transportSelection,
    renderContainerDetailsByJourneyId,
    getJourneyByContainerIds,
    companyAutoComplete,
    locationCodeAutoComplete,
    warningThresholds,
    imoClassAutoComplete,
  }: Props) => {
    const { companyStore, appStore, yardBlockStore, occupancyStore, capacitySettingsStore } =
      useYardStores()

    const allocationRuleThreshold = warningThresholds?.allocationRule ?? null
    const blockThreshold = warningThresholds?.block ?? null
    const yardThreshold = warningThresholds?.yard ?? null

    useEffect(() => {
      capacitySettingsStore.setThresholds({
        allocationRule: allocationRuleThreshold,
        block: blockThreshold,
        yard: yardThreshold,
      })
    }, [capacitySettingsStore, allocationRuleThreshold, blockThreshold, yardThreshold])

    const {
      loading,
      viewStore,
      setupStore,
      globalAllocationRulesViewStore,
      carrierVisitAllocationRulesViewStore,
      carrierVisitOverviewViewStore,
      housekeepingPlanAccumulatorViewStore,
      allocationRuleStore,
      isLeftDrawerOpen,
    } = useYardSetup({
      companyAutoComplete,
      locationCodeAutoComplete,
      imoClassAutoComplete,
      transportSelection,
      getJourneyByContainerIds,
    })

    // Triggered here (not in createYardStores) so the request runs after the
    // axios auth interceptor is attached.
    useEffect(() => {
      companyStore.loadCompanies().catch(error => {
        appStore.setShowAlert?.(
          'error',
          (error as { message?: string })?.message ?? 'Failed to load company master data',
        )
      })
    }, [companyStore, appStore])

    // Single source of truth for viewport width on the Yard page. Both the
    // canvas-width guard and the drawer-width hook receive this primitive so we
    // don't register multiple `resize` listeners for the same value. Prop-drill
    // (rather than context) because the consumer set is small and stable today
    // — three call sites, one intermediate component (YardLayout). Switch to a
    // YardLayoutContext if/when a fourth consumer lands.
    const viewportWidth = useViewportWidth()
    const containerDetailsDrawerWidth = useContainerDetailsDrawerWidth(
      viewStore,
      viewportWidth,
      isLeftDrawerOpen,
    )
    useCanvasWidthGuard({
      viewStore,
      isLeftDrawerOpen,
      isRightDrawerOpen: viewStore.isContainerDetailsOpen,
      rightDrawerWidth: containerDetailsDrawerWidth,
      viewportWidth,
    })

    if (loading) return <Loader show />

    return (
      <Box sx={{ width: '100%', height: '100vh', position: 'relative' }}>
        <YardHeader
          yardViewStore={viewStore}
          globalAllocationRulesViewStore={globalAllocationRulesViewStore}
          carrierVisitAllocationRulesViewStore={carrierVisitAllocationRulesViewStore}
          carrierVisitOverviewViewStore={carrierVisitOverviewViewStore}
          housekeepingPlanAccumulatorViewStore={housekeepingPlanAccumulatorViewStore}
          yardBlockViewStore={setupStore.yardBlockViewStore}
          flexibleBlockViewStore={setupStore.flexibleBlockViewStore}
          layoutObjectViewStore={setupStore.layoutObjectViewStore}
          berthViewStore={setupStore.berthViewStore}
          railTrackViewStore={setupStore.railTrackViewStore}
          roadNetworkViewStore={setupStore.roadNetworkViewStore}
        />
        <BlockAccessPointsPanel yardBlockViewStore={setupStore.yardBlockViewStore} />
        <YardFormDialogs
          yardBlockViewStore={setupStore.yardBlockViewStore}
          flexibleBlockViewStore={setupStore.flexibleBlockViewStore}
          layoutObjectViewStore={setupStore.layoutObjectViewStore}
          berthViewStore={setupStore.berthViewStore}
          railTrackViewStore={setupStore.railTrackViewStore}
          allocationRuleStore={allocationRuleStore}
          housekeepingPlanAccumulatorViewStore={housekeepingPlanAccumulatorViewStore}
          yardBlockStore={yardBlockStore}
          occupancyStore={occupancyStore}
        />
        <YardDrawers
          yardViewStore={viewStore}
          globalAllocationRulesViewStore={globalAllocationRulesViewStore}
          carrierVisitAllocationRulesViewStore={carrierVisitAllocationRulesViewStore}
          carrierVisitOverviewViewStore={carrierVisitOverviewViewStore}
          housekeepingPlanAccumulatorViewStore={housekeepingPlanAccumulatorViewStore}
        />
        <YardBlockContainers
          viewStore={viewStore}
          housekeepingPlanAccumulatorViewStore={housekeepingPlanAccumulatorViewStore}
          renderContainerDetailsByJourneyId={renderContainerDetailsByJourneyId}
          drawerWidth={containerDetailsDrawerWidth}
        />
        <YardLayout
          viewStore={viewStore}
          yardBlockViewStore={setupStore.yardBlockViewStore}
          flexibleBlockViewStore={setupStore.flexibleBlockViewStore}
          layoutObjectViewStore={setupStore.layoutObjectViewStore}
          berthViewStore={setupStore.berthViewStore}
          railTrackViewStore={setupStore.railTrackViewStore}
          roadNetworkViewStore={setupStore.roadNetworkViewStore}
          isLeftDrawerOpen={isLeftDrawerOpen}
          viewportWidth={viewportWidth}
        />
      </Box>
    )
  },
)
